import { useState } from 'react';

import { ApiError } from '../../lib/api';

/**
 * Turns a failed request into something a form can render.
 *
 * The split matters: the server returns per-field messages for a 422 and a
 * single message for everything else, and a form needs to put them in different
 * places - beside the input, or above the whole thing. Collapsing them into one
 * banner loses which field is wrong; showing only field errors loses "that
 * email or password is not right", which belongs to no field.
 */
export function useFormErrors() {
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  return {
    fields,
    message,

    clear: () => {
      setFields({});
      setMessage(null);
    },

    capture: (error: unknown) => {
      if (error instanceof ApiError) {
        const fieldErrors = error.fieldErrors;

        setFields(fieldErrors);
        // A 422 whose messages are all attached to inputs needs no banner - it
        // would just repeat what is already beside each field.
        setMessage(Object.keys(fieldErrors).length > 0 ? null : error.message);

        return;
      }

      /*
       * Not an ApiError, so the request never reached the server - the API
       * container is restarting, or the machine is offline. Saying "something
       * went wrong" here would be indistinguishable from a server bug; naming
       * the connection tells the person to check theirs.
       */
      setFields({});
      setMessage('Could not reach the server. Check your connection and try again.');
    },
  };
}
